package wat

import kotlinx.coroutines.await
import wat.ui.DebugLog
import kotlin.js.Promise

// Supabase client - uses CDN-loaded library via dynamic JS interop
object Supa {
    private const val URL = "https://wbgxbisoqxncqjpprors.supabase.co"
    private const val KEY = "sb_publishable_2WwQ27N6Y_qnl8mYwNhfNg_rbftI016"

    val client: dynamic by lazy {
        DebugLog.net("Supa.client init: URL=$URL")
        DebugLog.net("Supa.client init: KEY=${KEY.take(20)}...")
        val supabase = js("window.supabase")
        DebugLog.net("window.supabase = $supabase (type=${js("typeof window.supabase")})")
        DebugLog.net("supabase.createClient type = ${js("typeof supabase.createClient")}")
        val c = supabase.createClient(URL, KEY)
        DebugLog.ok("Supabase client created: $c")
        c
    }

    val auth: dynamic get() = client.auth

    fun from(table: String): dynamic = client.from(table)

    // Supabase v2 query builders are "thenable" but NOT real Promises.
    // Kotlin's Promise.await() needs a real Promise. Wrap with Promise.resolve().
    private suspend fun asPromise(thenable: dynamic): dynamic {
        val p = js("Promise.resolve(thenable)")
        return (p as Promise<dynamic>).await()
    }

    suspend fun signIn(email: String, password: String): dynamic {
        DebugLog.auth("signIn: $email")
        val creds = js("{}")
        creds.email = email
        creds.password = password
        val res = asPromise(auth.signInWithPassword(creds))
        if (res.error != null && res.error != undefined) {
            DebugLog.error("signIn error: ${res.error.message}")
        } else {
            DebugLog.ok("signIn success")
        }
        return res
    }

    suspend fun signUp(email: String, password: String): dynamic {
        DebugLog.auth("signUp: $email")
        val creds = js("{}")
        creds.email = email
        creds.password = password
        return asPromise(auth.signUp(creds))
    }

    suspend fun signOut(): dynamic {
        DebugLog.auth("signOut")
        return asPromise(auth.signOut())
    }

    suspend fun getSession(): dynamic {
        DebugLog.auth("getSession()...")
        val res = asPromise(auth.getSession())
        DebugLog.auth("getSession done")
        return res
    }

    fun onAuthStateChange(callback: (String, dynamic) -> Unit): dynamic {
        return auth.onAuthStateChange(callback)
    }

    suspend fun resetPassword(email: String): dynamic {
        val opts = js("{}")
        opts.redirectTo = js("window.location.origin")
        return asPromise(auth.resetPasswordForEmail(email, opts))
    }

    // DB Queries - all wrapped with Promise.resolve() for thenable compatibility
    suspend fun select(table: String, columns: String = "*"): dynamic {
        return asPromise(from(table).select(columns))
    }

    suspend fun selectWhere(table: String, column: String, value: dynamic, columns: String = "*"): dynamic {
        return asPromise(from(table).select(columns).eq(column, value))
    }

    suspend fun selectSingle(table: String, column: String, value: dynamic, columns: String = "*"): dynamic {
        return asPromise(from(table).select(columns).eq(column, value).single())
    }

    suspend fun insert(table: String, data: dynamic): dynamic {
        return asPromise(from(table).insert(data))
    }

    suspend fun update(table: String, data: dynamic, column: String, value: dynamic): dynamic {
        return asPromise(from(table).update(data).eq(column, value).select())
    }

    suspend fun upsert(table: String, data: dynamic, onConflict: String = "id"): dynamic {
        val opts = js("{}")
        opts.onConflict = onConflict
        return asPromise(from(table).upsert(data, opts).select())
    }

    suspend fun delete(table: String, column: String, value: dynamic): dynamic {
        return asPromise(from(table).delete().eq(column, value))
    }

    suspend fun rpc(fnName: String, params: dynamic = js("undefined")): dynamic {
        return asPromise(client.rpc(fnName, params))
    }

    suspend fun selectOrdered(table: String, columns: String, orderCol: String, ascending: Boolean = true, limitN: Int = 0): dynamic {
        val opts = js("{}")
        opts.ascending = ascending
        var q = from(table).select(columns).order(orderCol, opts)
        if (limitN > 0) q = q.limit(limitN)
        return asPromise(q)
    }

    suspend fun selectWhereOrdered(table: String, column: String, value: dynamic, columns: String, orderCol: String, ascending: Boolean = true, limitN: Int = 0): dynamic {
        val opts = js("{}")
        opts.ascending = ascending
        var q = from(table).select(columns).eq(column, value).order(orderCol, opts)
        if (limitN > 0) q = q.limit(limitN)
        return asPromise(q)
    }

    suspend fun selectWhereGt(table: String, col: String, value: dynamic, gtCol: String, gtVal: dynamic, columns: String, orderCol: String, ascending: Boolean = true, limitN: Int = 0): dynamic {
        val opts = js("{}")
        opts.ascending = ascending
        var q = from(table).select(columns).eq(col, value).gt(gtCol, gtVal).order(orderCol, opts)
        if (limitN > 0) q = q.limit(limitN)
        return asPromise(q)
    }
}
